import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useApp } from '@/context/AppContext';
import { Card, Delta, severityStyles } from '@/components/common';
import { HealthGauge } from '@/components/HealthGauge';
import { formatCurrency } from '@/lib/format';
import { AreaChart, Area, ResponsiveContainer, Tooltip } from 'recharts';
import { Sparkles, ArrowRight, ArrowUpRight, TrendingUp, TrendingDown, CheckCircle2, Clock } from 'lucide-react';

const Today = () => {
  const { t, tx, data, lang, setAdvisorOpen } = useApp();
  const navigate = useNavigate();
  const hour = new Date().getHours();
  const greetKey = hour < 12 ? 'today.greeting' : hour < 20 ? (lang === 'es' ? null : null) : null;

  const attention = data.todayAttention.map((id) => data.alerts.find((a) => a.id === id)).filter(Boolean);
  const upcoming = data.obligations.slice(0, 3);
  const pending = data.tasks.filter((x) => x.status !== 'done').slice(0, 3);

  return (
    <div>
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-6 lg:mb-8">
        <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight fp-page-title" data-testid="today-greeting">
          {t('today.greeting')}, {data.user}.
        </h1>
        <p className="mt-1.5 text-sm lg:text-base fp-page-subtitle">{t('today.intro')}</p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Health */}
        <Card className="lg:col-span-4 p-6 flex flex-col items-center justify-center" data-testid="today-health-card">
          <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-zinc-400 mb-2 self-start">{t('today.healthStatus')}</p>
          <HealthGauge score={data.health.score} label={tx(data.health.label)} size={190} />
          <div className="mt-4 w-full space-y-1.5 text-sm">
            <SignalRow up label={t('cash.title')} note={tx({ es: 'Tesorería saludable', en: 'Healthy cash' })} />
            <SignalRow up label={t('finance.revenue')} note={tx({ es: 'Ventas creciendo', en: 'Sales growing' })} />
            <SignalRow up={false} label={t('finance.margin')} note={tx({ es: 'Margen descendiendo', en: 'Margin declining' })} />
          </div>
        </Card>

        {/* Finance snapshot */}
        <Card className="lg:col-span-8 p-6" data-testid="today-finance-card">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-zinc-400">{t('finance.title')}</p>
            <button onClick={() => navigate('/app/finance')} className="text-cyan-600 text-xs font-medium hover:underline inline-flex items-center gap-1">{t('common.viewDetail')} <ArrowRight className="w-3 h-3" /></button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
            {data.finance.kpis.map((k) => (
              <div key={k.key}>
                <p className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1">{t(`finance.${k.key}`)}</p>
                <p className="font-heading text-lg lg:text-xl font-bold text-zinc-900 font-mono-num">{k.unit === 'pct' ? `${k.value}%` : formatCurrency(k.value, lang)}</p>
                <Delta value={k.delta} up={k.up} negativeDelta={k.negativeDelta} className="mt-0.5" />
              </div>
            ))}
          </div>
          <div className="h-28 -mx-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.finance.evolution}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#06B6D4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Tooltip formatter={(v) => formatCurrency(v, lang)} contentStyle={{ borderRadius: 12, border: '1px solid #e4e4e7', fontSize: 12 }} labelFormatter={() => ''} />
                <Area type="monotone" dataKey="revenue" stroke="#06B6D4" strokeWidth={2.5} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* AI insight banner */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-8 rounded-xl p-6 relative overflow-hidden text-white" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E1145 100%)' }} data-testid="today-ai-insight">
          <div className="grain absolute inset-0 opacity-20" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
              <span className="font-heading font-semibold">{t('today.aiInsight')}</span>
            </div>
            <p className="text-white/85 text-sm lg:text-[15px] leading-relaxed max-w-2xl">{tx(data.aiInsight)}</p>
            <button onClick={() => setAdvisorOpen(true)} data-testid="today-ask-advisor" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold bg-white/15 hover:bg-white/25 transition-colors rounded-lg px-3.5 py-2">
              {tx({ es: 'Preguntar al Advisor', en: 'Ask the Advisor' })} <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        {/* Requires attention */}
        <Card className="lg:col-span-4 lg:row-span-2 p-6 bg-rose-50/40 border-rose-100" data-testid="today-attention">
          <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-rose-500 mb-1">{t('today.requiresAttention')}</p>
          <p className="text-xs text-zinc-500 mb-4">{t('today.requiresAttentionDesc')}</p>
          <div className="space-y-3">
            {attention.map((a) => {
              const s = severityStyles[a.severity];
              return (
                <button key={a.id} onClick={() => navigate('/app/alerts')} data-testid={`attention-${a.id}`} className="w-full text-left bg-white rounded-lg border border-zinc-200 p-3.5 hover:border-zinc-300 transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full ${s.dot}`} />
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${s.text}`}>{t(`alerts.${a.severity}`)}</span>
                  </div>
                  <p className="text-sm font-medium text-zinc-800 leading-snug">{tx(a.title)}</p>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Pending */}
        <Card className="lg:col-span-4 p-6" data-testid="today-pending">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-zinc-400">{t('today.pending')}</p>
            <button onClick={() => navigate('/app/tasks')} className="text-cyan-600 text-xs font-medium hover:underline">{t('common.viewAll')}</button>
          </div>
          <div className="space-y-2.5">
            {pending.map((p) => (
              <div key={p.id} className="flex items-center gap-3 text-sm">
                <Clock className="w-4 h-4 text-zinc-300 shrink-0" />
                <span className="text-zinc-700 leading-snug">{tx(p.title)}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Upcoming */}
        <Card className="lg:col-span-4 p-6" data-testid="today-upcoming">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-zinc-400">{t('today.upcoming')}</p>
            <button onClick={() => navigate('/app/calendar')} className="text-cyan-600 text-xs font-medium hover:underline">{t('common.viewAll')}</button>
          </div>
          <div className="space-y-3">
            {upcoming.map((o) => (
              <div key={o.id} className="flex items-center gap-3">
                <div className="flex flex-col items-center justify-center w-10 h-10 rounded-lg bg-cyan-50 text-cyan-700 shrink-0">
                  <span className="text-sm font-bold leading-none font-heading">{o.day}</span>
                </div>
                <span className="text-sm text-zinc-700 leading-snug">{tx(o.title)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

const SignalRow = ({ up, label, note }) => (
  <div className="flex items-center gap-2">
    {up ? <TrendingUp className="w-4 h-4 text-emerald-500" /> : <TrendingDown className="w-4 h-4 text-rose-500" />}
    <span className="text-zinc-600">{note}</span>
  </div>
);

export default Today;
